import {createSlice} from '@reduxjs/toolkit';
import getSetting from '../../utils/settings';
import * as thunks from './thunks';
import {removeCouponCode} from "./thunks";
import constants from './constants';

const slice = createSlice({
    name: 'checkout',
    initialState: {
        isLoading: true,
        isUpdatingCheckout: false,
        currency: getSetting('currency'),
        product: null,
        plan: null,
        schedule: null,
        activePaymentMethod: '',
        customer: constants.DEFAULT_CUSTOMER,
        cart: null,
        cartTotals: [],
        status: {
            isBeforeProcessing: false,
            isBeforeOrderProcessing: false,
            isProcessing: false,
            isValidating: false,
            processingOrder: false,
            isAfterProcessing: false,
            isComplete: false,
            isAddingCoupon: false,
            isLoadingCustomer: false
        },
        notices: [],
        errors: [],
        response: {},
        coupon: {
            code: '',
            errors: []
        },
        order: null,
        license: null
    },
    reducers: {
        setLoading: (state, action) => {
            state.isLoading = action.payload
        },
        setProduct: (state, action) => {
            state.isLoading = false;
            state.product = action.payload;
        },
        setCheckout: (state, action) => {
            state.cart = action.payload.cart;
            state.billing = action.payload.billing;
        },
        setCart: (state, action) => {
            state.cart = action.payload;
        },
        setOrderPayData: (state, action) => {
            state.cart = action.payload.cart;
            state.currency = action.payload.currency;
            state.customer = action.payload.customer;
            state.order = action.payload.order;
        },
        updateBillingData: (state, action) => {
            const data = action.payload;
            state.billing = {...state.billing, ...data};
        },
        setCalculating: (state, action) => {
            slice.caseReducers.resetStatus(state, action);
            state.status.isCalculating = action.payload;
        },
        setBeforeProcessing: (state, action) => {
            slice.caseReducers.resetStatus(state, action);
            state.status.isBeforeProcessing = action.payload;
        },
        setProcessing: (state, action) => {
            slice.caseReducers.resetStatus(state, action);
            state.status.isProcessing = true;
        },
        setProcessingOrder: (state, action) => {
            slice.caseReducers.resetStatus(state, action);
            state.status.processingOrder = action.payload;
        },
        setAfterProcessing: (state, action) => {
            slice.caseReducers.resetStatus(state, action);
            state.status.isAfterProcessing = true;
            state.response = action.payload;
        },
        setComplete: (state, action) => {
            slice.caseReducers.resetStatus(state, action);
            state.status.isComplete = true;
        },
        setBeforeOrderProcessing: (state, action) => {
            slice.caseReducers.resetStatus(state, action);
            state.status.isBeforeOrderProcessing = action.payload;
        },
        resetStatus: (state, action) => {
            for (const [key, value] of Object.entries(state.status)) {
                state.status[key] = false;
            }
        },
        addNotices: (state, action) => {
            const ids = action.payload.map(notice => notice.id);
            // filter existing notices
            const filteredNotices = state.notices.filter(notice => !ids.includes(notice.id));
            state.notices = [...filteredNotices, ...action.payload];
        },
        removeNotice: (state, action) => {
            state.notices = state.notices.filter(notice => notice.id !== action.payload);
        },
        clearNotices: (state) => {
            state.notices = [];
            state.errors = [];
        },
        setOrderData: (state, action) => {
            const {order, license} = action.payload;
            state.order = order;
            state.license = license;
        },
        setCustomer: (state, action) => {
            state.customer = action.payload;
        },
        setBillingAddress: (state, action) => {
            state.customer = {...state.customer, billingAddress: {...state.customer.billingAddress, ...action.payload}};
        },
        setBillingEmail: (state, action) => {
            state.customer = {...state.customer, billingEmail: action.payload};
        },
        resetCustomer: (state, action) => {
            state.customer = constants.DEFAULT_CUSTOMER;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(thunks.addToCart.pending, state => {
                state.isLoading = true;
            })
            .addCase(thunks.addToCart.fulfilled, (state, action) => {
                state.isLoading = false;
                state.cart = action.payload.cart;
                state.customer = action.payload.customer;
            })
            .addCase(thunks.addToCart.rejected, (state, action) => {
                //state.errors = [...state.errors, action.payload];
                state.isLoading = false;
            })
            .addCase(thunks.updateCheckoutData.pending, (state, action) => {
                slice.caseReducers.resetStatus(state, action);
                state.status.isCalculating = true;
            })
            .addCase(thunks.updateCheckoutData.fulfilled, (state, action) => {
                state.status.isCalculating = false;
                if (action.payload?.cart) {
                    state.cart = action.payload.cart;
                }
            })
            .addCase(thunks.updateCheckoutData.rejected, (state, action) => {
                state.status.isCalculating = false;
                if (action?.errors) {
                    state.errors = [...state.errors, action.errors];
                } else if (action?.error) {
                    state.errors = [...state.errors, action.error];
                }
            })
            .addCase(thunks.addCouponCode.pending, (state, action) => {
                state.status.isAddingCoupon = true;
            })
            .addCase(thunks.addCouponCode.fulfilled, (state, action) => {
                state.status.isAddingCoupon = false;
                state.cart = {...state.cart, ...action.payload.cart};
            })
            .addCase(thunks.addCouponCode.rejected, (state, action) => {
                state.status.isAddingCoupon = false;
            })
            .addCase(thunks.removeCouponCode.pending, (state, action) => {
                state.status.isRemovingCoupon = true;
            })
            .addCase(thunks.removeCouponCode.fulfilled, (state, action) => {
                state.status.isRemovingCoupon = false;
                state.cart = {...state.cart, ...action.payload.cart};
            })
            .addCase(thunks.removeCouponCode.rejected, (state, action) => {
                state.status.isRemovingCoupon = false;
            })
            .addCase(thunks.fetchCustomer.pending, (state, action) => {
                state.status.isLoadingCustomer = true;
            })
            .addCase(thunks.fetchCustomer.fulfilled, (state, action) => {
                state.customer = action.payload;
            })
            .addCase(thunks.fetchCustomer.rejected, (state, action) => {
                state.status.isLoadingCustomer = false;
                if (action?.errors) {
                    state.errors = [...state.errors, action.errors];
                } else if (action?.error) {
                    state.errors = [...state.errors, action.error];
                }
            });
    }
});

export default slice.reducer;

export const {
    setLoading,
    setCart,
    setCheckout,
    updateBillingData,
    setCalculating,
    setBeforeProcessing,
    setProcessing,
    setProcessingOrder,
    setAfterProcessing,
    setBeforeOrderProcessing,
    setComplete,
    resetStatus,
    addNotices,
    removeNotice,
    clearNotices,
    resetCustomer,
    setOrderPayData
} = slice.actions;

const {setBillingAddress, setBillingEmail} = slice.actions;
const customer = {
    setBillingEmail,
    setBillingAddress
}

export {
    customer
}

export const {
    addCouponCode: addCoupon,
    removeCouponCode: removeCoupon,
    fetchCustomer,
    addToCart
} = thunks